import React from 'react';
import Header from './Header';
import Footer from './Footer';

const WhitepaperPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Deepnets Utility Token: $DNET</h1>
          <div className="text-gray-500 text-sm">
            Last updated: March 2026 — Added Optional Staking Pool for Long-Term Ecosystem Alignment section.
          </div>
        </div>

        <div className="space-y-10 text-gray-300 leading-relaxed">
          {/* Abstract */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Abstract</h2>
            <p>
              $DNET is a pure utility token designed to power premium features on deepnets.ai, the platform
              dedicated to making the crypto trenches safe through real-time, AI-driven analysis of new tokens.
            </p>
            <p className="mt-3">
              Existing research remains completely free for everyone. New research triggers, priority alerts on
              "clean" tokens, and API access require burning $DNET. Every single token spent is permanently
              removed from circulation.
            </p>
            <div className="mt-4 p-4 bg-crypto-gray border border-gray-700 rounded-lg">
              <p className="text-gray-400 text-sm">
                <strong className="text-white">Important:</strong> $DNET is <strong>not</strong> an investment.
                It confers <strong>zero</strong> equity, ownership, dividends, or profit rights in deepnets.ai.
                Its only purpose is to unlock platform utility.
              </p>
            </div>
          </section>

          {/* The Problem */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">The Problem</h2>
            <p>
              The crypto trenches are dangerous. New tokens launch every minute. Most are scams, rugs, or
              honeypots. Retail traders and even experienced analysts lack affordable, real-time deep research.
              Building and running the necessary AI infrastructure is expensive.
            </p>
            <p className="mt-3">
              Traditional subscription models exclude the very community that needs protection most. We needed
              a native, on-chain way to fund the mission while keeping core research open to all.
            </p>
          </section>

          {/* Our Solution */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Our Solution: Deepnets.ai + $DNET</h2>
            <p>
              Deepnets.ai delivers production-grade token analysis (contract security, liquidity checks, holder
              distribution, social sentiment, and "clean token" scoring).
            </p>
            <p className="mt-3">
              All historical reports and the public watchlist are free forever.
            </p>
            <p className="mt-3">
              To fund ongoing research and prevent abuse, we offer multiple access paths — $DNET burns for per-use credits,
              $DNET holding for permanent tier access, or conventional monthly subscriptions for those who prefer traditional payments:
            </p>
            <ul className="mt-3 space-y-2 ml-6">
              <li className="flex items-start">
                <span className="text-crypto-accent mr-2">•</span>
                <span>Burn $DNET → instantly receive credits.</span>
              </li>
              <li className="flex items-start">
                <span className="text-crypto-accent mr-2">•</span>
                <span>1 credit = 1 new research request (~$0.15 USD equivalent).</span>
              </li>
              <li className="flex items-start">
                <span className="text-crypto-accent mr-2">•</span>
                <span>Hold $DNET → unlock tier access for as long as you hold.</span>
              </li>
              <li className="flex items-start">
                <span className="text-crypto-accent mr-2">•</span>
                <span>Subscribe monthly → conventional payment option ($10/mo Watcher, $150/mo Deep Diver).</span>
              </li>
              <li className="flex items-start">
                <span className="text-crypto-accent mr-2">•</span>
                <span>Priority notifications when genuinely clean tokens hit the watchlist.</span>
              </li>
              <li className="flex items-start">
                <span className="text-crypto-accent mr-2">•</span>
                <span>API access for bots and power users.</span>
              </li>
            </ul>
          </section>

          {/* Traditional Subscriptions */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Traditional Subscriptions</h2>
            <p>
              For users who prefer conventional payments over token holdings, we offer monthly subscriptions:
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">👁️ Watcher — $10/mo</h3>
                <p className="text-gray-400 text-sm">
                  Access to new research under 2 hours old. Priority watchlist alerts. No $DNET holding required.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">🔱 Deep Diver — $150/mo</h3>
                <p className="text-gray-400 text-sm">
                  Everything in Watcher plus full API access for programmatic research. Ideal for traders and developers.
                </p>
              </div>
            </div>
            <p className="mt-4 text-gray-400 text-sm">
              Subscriptions are an <strong className="text-white">alternative</strong> to $DNET holding, not a replacement.
              If you hold enough $DNET for a tier, you don't need to subscribe — your access is permanent as long as you hold.
              Both options are valid; choose what works best for you.
            </p>
          </section>

          {/* Token Utility & Burn Mechanic */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">$DNET Token Utility & 100% Burn Mechanic</h2>
            <div className="space-y-4">
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">🔥 100% Burn on Every Use</h3>
                <p className="text-gray-400 text-sm">
                  When you purchase credits, every $DNET is permanently burned. Supply shrinks with real
                  platform usage. This creates deflationary pressure and aligns incentives.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">📊 Dynamic Pricing</h3>
                <p className="text-gray-400 text-sm">
                  The system always targets ~$0.15 USD per credit using real-time market data. You always
                  know the exact USD cost before burning.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">📦 Bundles & Perks</h3>
                <p className="text-gray-400 text-sm">
                  Volume discounts, Monthly Unlimited option, and free daily credits for larger holders.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">💎 Holder Benefits</h3>
                <p className="text-gray-400 text-sm">
                  The more the platform is used, the more supply is permanently removed, benefiting every holder.
                </p>
              </div>
            </div>
          </section>

          {/* Optional Staking Pool */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Optional Staking Pool for Long-Term Ecosystem Alignment</h2>
            <p>
              As an additional, fully optional ecosystem feature, deepnets.ai has established a permissionless
              staking pool built on{' '}
              <a href="https://tibane.com" target="_blank" rel="noopener noreferrer" className="text-crypto-accent hover:text-crypto-accent/80 transition-colors">
                Tibane Labs
              </a>{' '}
              infrastructure. This pool is a voluntary, time-weighted loyalty mechanism designed to recognize
              holders who maintain sustained commitment to the deepnets.ai ecosystem.
            </p>

            <div className="mt-4 p-4 bg-crypto-gray border border-gray-700 rounded-lg">
              <p className="text-gray-400 text-sm">
                <strong className="text-white">Note:</strong> This is <strong>not</strong> Solana protocol staking
                (i.e., delegating SOL to validators for network consensus). It is a custom, permissionless pool
                funded by redirecting a portion of the creator's own SOL fees. It does not alter the core utility
                or consumable nature of $DNET.
              </p>
            </div>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">How It Works</h3>
            <div className="space-y-4">
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Pool Funding</h4>
                <p className="text-gray-400 text-sm">
                  42% of the creator's personal creator-fee SOL (generated from $DNET trading volume) is
                  directed into the staking pool. These are funds that would otherwise go to the creator —
                  they are voluntarily redirected to the pool as a gesture of ecosystem alignment.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Time-Weighted Staking</h4>
                <p className="text-gray-400 text-sm">
                  Staking weight grows over time according to the formula:
                </p>
                <div className="mt-2 p-3 bg-crypto-dark rounded font-mono text-sm text-crypto-accent">
                  weight = 1 − e<sup>−age/τ</sup> &nbsp;&nbsp;where τ = 21 days
                </div>
                <p className="text-gray-400 text-sm mt-2">
                  Participants who stake for longer accumulate greater weight, and therefore a larger share
                  of the pool. Early unstakers receive proportionally lower weight. This incentivizes long-term
                  alignment and commitment rather than short-term participation.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Zero Lockup, Zero Cooldown</h4>
                <p className="text-gray-400 text-sm">
                  There is no mandatory lockup period and no cooldown. Participants may unstake at any time.
                  The time-weighted formula naturally differentiates between short-term and long-term
                  participants without imposing restrictions.
                </p>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Permissionless & On-Chain</h4>
                <p className="text-gray-400 text-sm">
                  The pool operates on Tibane Labs' permissionless on-chain infrastructure. Staking and
                  unstaking are executed directly via smart contract interactions — no intermediary
                  or approval is required.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-crypto-gray border border-gray-700 rounded-lg">
              <p className="text-gray-400 text-sm">
                <strong className="text-white">Important:</strong> $DNET's primary and defining utility remains
                consumption via burning for platform features (research credits, priority alerts, API access).
                The staking pool is an additional, optional mechanism and does not change the token's status as
                a pure utility token with no equity, ownership, dividends, or profit rights. Participation in
                the staking pool does not create any new rights or entitlements beyond what is described in
                this section.
              </p>
            </div>
          </section>

          {/* Tokenomics */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Tokenomics (Fully Fair Launch)</h2>
            <div className="mb-4 p-4 bg-crypto-gray border border-crypto-accent/30 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Token Mint Address</div>
              <a
                href="https://pump.fun/coin/93VXFhKsGXsraivwdwAwoVXHKd4U1TFL64oQYpVjpump"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crypto-accent hover:text-crypto-accent/80 font-mono text-sm break-all transition-colors"
              >
                93VXFhKsGXsraivwdwAwoVXHKd4U1TFL64oQYpVjpump
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <div className="text-crypto-accent text-2xl font-bold">1,000,000,000</div>
                <div className="text-gray-400 text-sm mt-1">Total Supply (fixed)</div>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <div className="text-crypto-accent text-2xl font-bold">pump.fun</div>
                <div className="text-gray-400 text-sm mt-1">Launch Platform (Solana)</div>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <div className="text-crypto-accent text-2xl font-bold">4.5%</div>
                <div className="text-gray-400 text-sm mt-1">Founder Allocation (at creation)</div>
              </div>
              <div className="p-4 bg-crypto-gray border border-gray-700 rounded-lg">
                <div className="text-crypto-accent text-2xl font-bold">100%</div>
                <div className="text-gray-400 text-sm mt-1">Fair Launch, Zero Pre-mine</div>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-gray-400 text-sm">
              <p>
                <strong className="text-white">Liquidity:</strong> Automatic graduation to PumpSwap with LP tokens burned.
              </p>
              <p>
                <strong className="text-white">Revenue for the Platform:</strong> Creator fees from all trading volume
                (tiered by market cap). Of the creator-fee SOL, 42% is directed to the optional staking pool
                (see above) and the remainder supports platform operations. User burns go 100% to permanent
                reduction. Never to any team wallet.
              </p>
              <p>
                <strong className="text-white">Dev Tokens:</strong> The founder allocation (4.5%) is{' '}
                <strong className="text-white">locked</strong>. These tokens cannot be sold or transferred
                until unlock conditions are met, ensuring long-term alignment with the community.
              </p>
              <p className="text-white font-semibold">
                No Hidden Allocations. Dev tokens locked.
              </p>
            </div>
          </section>

          {/* How It Works */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">How It Works (User Flow)</h2>
            <div className="space-y-3">
              {[
                'Connect Solana wallet on deepnets.ai.',
                'See current $DNET balance and live credit pricing.',
                'Choose credits or bundle → confirm exact burn amount (always ~$0.15 USD each).',
                'Burn transaction → credits appear instantly + on-chain proof.',
                'Trigger new research, get alerts, or use the API.',
              ].map((step, i) => (
                <div key={i} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-crypto-accent text-crypto-dark rounded-full flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <p className="pt-1">{step}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-crypto-accent font-medium">All existing research stays free.</p>
          </section>

          {/* Legal */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Legal & Important Disclaimers</h2>
            <div className="p-6 bg-crypto-gray border border-gray-700 rounded-lg space-y-3 text-gray-400 text-sm">
              <p>
                $DNET is a utility token for accessing features on deepnets.ai only.
              </p>
              <p>
                It is <strong className="text-white">not</strong> an investment contract, security, or equity in
                deepnets.ai or any related entity.
              </p>
              <p>
                There is no expectation of profit from the efforts of the team.
              </p>
              <p>
                Token value derives solely from demand for platform usage and may go to zero.
              </p>
              <p>
                Past performance is not indicative of future results.
              </p>
              <p>
                Deepnets.ai research is for informational purposes only, not financial advice. Always do your own research.
              </p>
              <p>
                Users in restricted jurisdictions should consult local regulations.
              </p>
              <p>
                By using $DNET you agree that it functions purely as a consumable access credential.
              </p>
              <p className="pt-2 border-t border-gray-600">
                <strong className="text-white">Regarding the Optional Staking Pool:</strong> Participation
                in the staking pool is entirely voluntary and does not constitute an investment contract or
                create any expectation of profits derived from the efforts of others. The pool is funded
                solely by a voluntary redirection of the creator's personal creator-fee SOL. No guarantees
                are made regarding amounts received from the pool, which depend entirely on trading volume,
                overall participation levels, and individual staking duration. The staking pool does not
                alter any of the disclaimers stated above. $DNET remains a pure utility token regardless
                of whether a holder chooses to participate in the pool.
              </p>
            </div>
          </section>

          {/* Vision */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Vision</h2>
            <p>
              We launched $DNET because we believe in the system. We are eating our own dogfood, burning our own
              tokens alongside users, and funding the mission through transparent creator fees and genuine utility.
            </p>
            <p className="mt-3">
              The trenches are safer when everyone has access to real research.
            </p>
            <p className="mt-6 text-center text-xl font-bold text-crypto-accent">
              Welcome to the safe side of the trenches.
            </p>
            <p className="mt-4 text-center text-gray-500">
              deepnets.ai | <strong className="text-white">$DNET</strong> | Burn. Research. Protect.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WhitepaperPage;
